import React, { useEffect, useState } from 'react';
import { pricingApi } from '../../services/api';
import api from '../../services/api';
import {
  PlusIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from 'components/icons';
import Modal from '../ui/Modal';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface PricingItem {
  id: string;
  itemType: string;
  clin?: string | null;
  description: string;
  laborCategory?: string | null;
  baseRate?: string | number | null;
  hours?: string | number | null;
  unitCost?: string | number | null;
  quantity?: string | number | null;
  periodOfPerformance?: string | null;
  periodIndex: number;
  loadedRate?: string | number | null;
  extendedTotal?: string | number | null;
  notes?: string | null;
}

interface ComputedTotals {
  byPeriod: Record<string, { itemCount: number; total: number }>;
  byItemType: Record<string, { itemCount: number; total: number }>;
  grandTotal: number;
  itemCount: number;
}

interface ComputeResult {
  items: any[];
  totals: ComputedTotals;
  profileSummary: {
    fringeRate: number;
    overheadRate: number;
    gaRate: number;
    profitRate: number;
    escalationRate: number;
    odcMarkup: number;
    dcaaApproved: boolean;
    casCovered: boolean;
    auditPosture: string | null;
  };
}

interface AddForm {
  itemType: string;
  clin: string;
  description: string;
  laborCategory: string;
  baseRate: string;
  hours: string;
  unitCost: string;
  quantity: string;
  periodOfPerformance: string;
  periodIndex: string;
  notes: string;
}

const EMPTY_ADD: AddForm = {
  itemType: 'labor',
  clin: '',
  description: '',
  laborCategory: '',
  baseRate: '',
  hours: '',
  unitCost: '',
  quantity: '1',
  periodOfPerformance: 'Base Year',
  periodIndex: '0',
  notes: '',
};

const ITEM_TYPES = [
  { value: 'labor', label: 'Labor' },
  { value: 'odc', label: 'ODC' },
  { value: 'material', label: 'Material' },
  { value: 'travel', label: 'Travel' },
  { value: 'subcontract', label: 'Subcontract' },
  { value: 'fixed_fee', label: 'Fixed Fee' },
  { value: 'other', label: 'Other' },
];

const fmtMoney = (v: any): string => {
  if (v == null || v === '') return '—';
  const n = parseFloat(String(v));
  if (isNaN(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PricingModal: React.FC<{ proposalId: string; onClose: () => void }> = ({ proposalId, onClose }) => {
  const [computed, setComputed] = useState<ComputeResult | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>(EMPTY_ADD);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [computeRes, profileRes] = await Promise.all([
        pricingApi.compute(proposalId),
        pricingApi.getProfile(),
      ]);
      setComputed(computeRes.data?.data);
      setProfile(profileRes.data?.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  const startAdd = () => {
    setEditingId(null);
    setAddForm(EMPTY_ADD);
    setShowAdd(true);
  };

  const startEdit = (item: PricingItem) => {
    setEditingId(item.id);
    setAddForm({
      itemType: item.itemType,
      clin: item.clin || '',
      description: item.description,
      laborCategory: item.laborCategory || '',
      baseRate: item.baseRate != null ? String(item.baseRate) : '',
      hours: item.hours != null ? String(item.hours) : '',
      unitCost: item.unitCost != null ? String(item.unitCost) : '',
      quantity: item.quantity != null ? String(item.quantity) : '1',
      periodOfPerformance: item.periodOfPerformance || 'Base Year',
      periodIndex: String(item.periodIndex),
      notes: item.notes || '',
    });
    setShowAdd(true);
  };

  const submitItem = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: any = {
        itemType: addForm.itemType,
        clin: addForm.clin || null,
        description: addForm.description,
        laborCategory: addForm.laborCategory || null,
        baseRate: addForm.baseRate ? parseFloat(addForm.baseRate) : null,
        hours: addForm.hours ? parseFloat(addForm.hours) : null,
        unitCost: addForm.unitCost ? parseFloat(addForm.unitCost) : null,
        quantity: addForm.quantity ? parseFloat(addForm.quantity) : 1,
        periodOfPerformance: addForm.periodOfPerformance || null,
        periodIndex: parseInt(addForm.periodIndex, 10) || 0,
        notes: addForm.notes || null,
      };
      if (editingId) {
        await pricingApi.updateItem(editingId, payload);
      } else {
        await pricingApi.createItem(proposalId, payload);
      }
      setShowAdd(false);
      setEditingId(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this line item?')) return;
    try {
      await pricingApi.deleteItem(id);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    await load();
    setRecomputing(false);
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get(pricingApi.exportUrl(proposalId), { responseType: 'blob' });
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pricing_${proposalId.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Pricing xlsx downloaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const profileMissing =
    !profile ||
    (!profile.fringeRate && !profile.overheadRate && !profile.gaRate && !profile.profitRate);

  // Group items by period
  const byPeriod = new Map<string, any[]>();
  if (computed) {
    for (const item of computed.items) {
      const key = item.periodOfPerformance || `Period ${item.periodIndex}`;
      if (!byPeriod.has(key)) byPeriod.set(key, []);
      byPeriod.get(key)!.push(item);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Pricing — Line Items & Totals"
      size="xl"
      description={
        profile
          ? `Profile: Fringe ${(profile.fringeRate * 100 || 0).toFixed(2)}% · OH ${(profile.overheadRate * 100 || 0).toFixed(2)}% · G&A ${(profile.gaRate * 100 || 0).toFixed(2)}% · Profit ${(profile.profitRate * 100 || 0).toFixed(2)}% · Escalation ${(profile.escalationRate * 100 || 0).toFixed(2)}%${profile.dcaaApproved ? ' · DCAA approved' : ''}`
          : undefined
      }
      footer={
        <>
          <button onClick={recompute} disabled={recomputing} className="btn-secondary">
            <ArrowPathIcon className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {recomputing ? 'Recomputing…' : 'Refresh'}
          </button>
          <button
            onClick={downloadXlsx}
            disabled={downloading || !computed || computed.items.length === 0}
            className="btn-primary"
          >
            <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
            {downloading ? 'Downloading…' : 'Download xlsx'}
          </button>
        </>
      }
    >
      <div className="flex flex-col">

        {/* Profile-missing banner */}
        {profileMissing && (
          <div className="m-5 p-3 rounded bg-amber-50 border border-amber-300 text-sm flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-amber-900">Pricing Profile not set.</strong>
              <span className="text-content"> Loaded rates will be 0 until you configure indirect rates. </span>
              <Link to="/pricing-profile" className="text-purple-700 underline">Set up Profile →</Link>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading ? (
            <div className="text-content-muted text-sm">Loading…</div>
          ) : !computed || computed.items.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-border-strong rounded-lg">
              <p className="text-content-muted text-sm mb-3">No line items yet.</p>
              <button
                onClick={startAdd}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-500 hover:to-purple-500"
              >
                <PlusIcon className="w-4 h-4" /> Add First Line Item
              </button>
            </div>
          ) : (
            <>
              {Array.from(byPeriod.entries()).sort(([, a], [, b]) => (a[0]?.periodIndex || 0) - (b[0]?.periodIndex || 0)).map(([period, items]) => {
                const periodTotal = items.reduce((s, i) => s + (i.extendedTotal || 0), 0);
                return (
                  <div key={period} className="bg-surface-sunk rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-content">{period}</h3>
                      <span className="text-sm text-content-muted">
                        Subtotal: <span className="font-semibold text-content">{fmtMoney(periodTotal)}</span>
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-content-muted border-b">
                          <th className="py-1.5 pr-2">CLIN</th>
                          <th className="py-1.5 pr-2">Description</th>
                          <th className="py-1.5 pr-2">Type</th>
                          <th className="py-1.5 pr-2 text-right">Hours/Qty</th>
                          <th className="py-1.5 pr-2 text-right">Rate / Unit</th>
                          <th className="py-1.5 pr-2 text-right">Loaded</th>
                          <th className="py-1.5 pr-2 text-right">Extended</th>
                          <th className="py-1.5 w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item: any) => (
                          <tr key={item.id} className="border-b last:border-0 hover:bg-surface">
                            <td className="py-1.5 pr-2 font-mono text-xs">{item.clin || '—'}</td>
                            <td className="py-1.5 pr-2">
                              <div className="text-content">{item.description}</div>
                              {item.laborCategory && (
                                <div className="text-xs text-content-muted">{item.laborCategory}</div>
                              )}
                            </td>
                            <td className="py-1.5 pr-2">
                              <span className="inline-block px-1.5 py-0.5 rounded bg-gray-200 text-content text-xs">
                                {item.itemType}
                              </span>
                            </td>
                            <td className="py-1.5 pr-2 text-right text-content">
                              {item.itemType === 'labor'
                                ? `${item.hours || 0} hr`
                                : `${item.quantity || 1}×`}
                            </td>
                            <td className="py-1.5 pr-2 text-right text-content">
                              {item.itemType === 'labor'
                                ? fmtMoney(item.baseRate)
                                : fmtMoney(item.unitCost)}
                            </td>
                            <td className="py-1.5 pr-2 text-right text-content">
                              {item.itemType === 'labor' ? fmtMoney(item.loadedRate) : '—'}
                            </td>
                            <td className="py-1.5 pr-2 text-right font-medium text-content">
                              {fmtMoney(item.extendedTotal)}
                            </td>
                            <td className="py-1.5 text-right">
                              <button
                                onClick={() => startEdit(item)}
                                className="text-xs text-purple-700 hover:text-purple-900 mr-2"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => remove(item.id)}
                                className="text-content-subtle hover:text-red-600"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4 inline" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}

              <div className="border-t pt-4 flex items-center justify-between">
                <button
                  onClick={startAdd}
                  className="inline-flex items-center gap-1 text-sm text-purple-700 hover:text-purple-900 font-medium"
                >
                  <PlusIcon className="w-4 h-4" /> Add Line Item
                </button>
                <div className="text-right">
                  <div className="text-xs text-content-muted">Grand Total</div>
                  <div className="text-2xl font-bold text-content">
                    {fmtMoney(computed.totals.grandTotal)}
                  </div>
                  <div className="text-xs text-content-muted">{computed.totals.itemCount} line items</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Add/Edit modal — nested dialog; headlessui stacks these correctly
            and returns focus to this modal when the inner one closes. */}
        <Modal
          open={showAdd}
          onClose={() => setShowAdd(false)}
          dismissOnOverlayClick={false}
          title={`${editingId ? 'Edit' : 'Add'} Line Item`}
          size="lg"
          footer={
            <>
              <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" form="pricing-line-item-form" disabled={saving} className="btn-primary">
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Item'}
              </button>
            </>
          }
        >
              <form id="pricing-line-item-form" onSubmit={submitItem} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Type *">
                    <select
                      value={addForm.itemType}
                      onChange={(e) => setAddForm({ ...addForm, itemType: e.target.value })}
                      className="input"
                    >
                      {ITEM_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="CLIN">
                    <input
                      type="text"
                      value={addForm.clin}
                      onChange={(e) => setAddForm({ ...addForm, clin: e.target.value })}
                      placeholder="0001 or 0001AA"
                      className="input"
                    />
                  </Field>
                </div>
                <Field label="Description *">
                  <input
                    type="text"
                    required
                    value={addForm.description}
                    onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                    placeholder="Sr Project Manager (Base Year)"
                    className="input"
                  />
                </Field>

                {addForm.itemType === 'labor' && (
                  <>
                    <Field label="Labor Category">
                      <select
                        value={addForm.laborCategory}
                        onChange={(e) => {
                          const cat = profile?.laborCategories?.find((c: any) => c.name === e.target.value);
                          setAddForm({
                            ...addForm,
                            laborCategory: e.target.value,
                            baseRate: cat ? String(cat.baseRate) : addForm.baseRate,
                          });
                        }}
                        className="input"
                      >
                        <option value="">— select to auto-fill base rate —</option>
                        {(profile?.laborCategories || []).map((c: any) => (
                          <option key={c.name} value={c.name}>{c.name} (${c.baseRate}/hr)</option>
                        ))}
                      </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Base Rate ($/hr) *">
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={addForm.baseRate}
                          onChange={(e) => setAddForm({ ...addForm, baseRate: e.target.value })}
                          placeholder="50.00"
                          className="input"
                        />
                      </Field>
                      <Field label="Hours *">
                        <input
                          type="number"
                          step="1"
                          required
                          value={addForm.hours}
                          onChange={(e) => setAddForm({ ...addForm, hours: e.target.value })}
                          placeholder="1920"
                          className="input"
                        />
                      </Field>
                    </div>
                  </>
                )}

                {addForm.itemType !== 'labor' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Unit Cost ($) *">
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={addForm.unitCost}
                        onChange={(e) => setAddForm({ ...addForm, unitCost: e.target.value })}
                        placeholder="250.00"
                        className="input"
                      />
                    </Field>
                    <Field label="Quantity">
                      <input
                        type="number"
                        step="1"
                        value={addForm.quantity}
                        onChange={(e) => setAddForm({ ...addForm, quantity: e.target.value })}
                        className="input"
                      />
                    </Field>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Period of Performance">
                    <input
                      type="text"
                      value={addForm.periodOfPerformance}
                      onChange={(e) => setAddForm({ ...addForm, periodOfPerformance: e.target.value })}
                      placeholder="Base Year, Option Year 1, etc."
                      className="input"
                    />
                  </Field>
                  <Field label="Period Index">
                    <select
                      value={addForm.periodIndex}
                      onChange={(e) => {
                        const idx = e.target.value;
                        const labelMap: Record<string, string> = {
                          '0': 'Base Year', '1': 'Option Year 1', '2': 'Option Year 2',
                          '3': 'Option Year 3', '4': 'Option Year 4',
                        };
                        setAddForm({ ...addForm, periodIndex: idx, periodOfPerformance: labelMap[idx] || addForm.periodOfPerformance });
                      }}
                      className="input"
                    >
                      <option value="0">0 = Base Year</option>
                      <option value="1">1 = Option Year 1</option>
                      <option value="2">2 = Option Year 2</option>
                      <option value="3">3 = Option Year 3</option>
                      <option value="4">4 = Option Year 4</option>
                    </select>
                  </Field>
                </div>

                <Field label="Notes">
                  <input
                    type="text"
                    value={addForm.notes}
                    onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                    className="input"
                  />
                </Field>

              </form>
        </Modal>
      </div>
    </Modal>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-content mb-1">{label}</label>
    {children}
  </div>
);

export default PricingModal;
